import { RefObject, useEffect } from 'react';

// Every HubSpot field container carries a data-hsfc-id ending in Field or FieldGroup
// (TextField, EmailField, PhoneField, CheckboxFieldGroup, DataPrivacyField, ...), so the
// guard works for any form shape instead of a fixed list of field types. Containers can
// nest - a DataPrivacyField wraps a CheckboxField - which is harmless since a field only
// ever blocks submission when it is both required and empty.
const FIELD_SELECTOR = '[data-hsfc-id$="Field"], [data-hsfc-id$="FieldGroup"]';

// The search box inside a dropdown's option list is always empty and belongs to no field.
const IGNORED_CONTROL_ANCESTOR = '.hsfc-DropdownOptions';

// Deliberately loose - it only has to catch addresses HubSpot itself would reject.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type FieldControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

function getFieldControls(field: HTMLElement) {
	return Array.from(field.querySelectorAll<FieldControl>('input:not([type="hidden"]), textarea, select')).filter(
		(control) => !control.closest(IGNORED_CONTROL_ANCESTOR),
	);
}

function getTrimmedValue(control: Element | null) {
	const value = (control as FieldControl | null)?.value;
	return typeof value === 'string' ? value.trim() : null;
}

function isHidden(field: HTMLElement) {
	// Fields hidden by conditional logic must not block submission. Walk no further than the
	// form itself - if the whole form is hidden, the button state is irrelevant anyway.
	let node: HTMLElement | null = field;

	while (node && node.tagName !== 'FORM') {
		if (node.hidden) return true;

		const { display, visibility } = window.getComputedStyle(node);
		if (display === 'none' || visibility === 'hidden') return true;

		node = node.parentElement;
	}

	return false;
}

function isRequired(field: HTMLElement) {
	// HubSpot marks required fields with an indicator in the label. Our checkbox groups hide
	// that label and render their own, so fall back to the controls' own required flags.
	if (field.querySelector('.hsfc-FieldLabel__RequiredIndicator')) return true;
	return getFieldControls(field).some((control) => control.required || control.getAttribute('aria-required') === 'true');
}

function isFilled(field: HTMLElement) {
	const controls = getFieldControls(field);

	// An unrecognised field with nothing to fill in must never block submission.
	if (!controls.length) return true;

	// Checkboxes and radios - single consent boxes as well as groups - need one selection.
	// The react-select replacement mirrors its selection onto these native inputs.
	const toggles = controls.filter((control) => control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio'));
	if (toggles.length) return toggles.some((toggle) => (toggle as HTMLInputElement).checked);

	const fileInputs = controls.filter((control) => control instanceof HTMLInputElement && control.type === 'file');
	if (fileInputs.length) return fileInputs.some((input) => !!(input as HTMLInputElement).files?.length);

	// HubSpot rejects an address without a domain and a top level domain, so a half typed one
	// should not enable the button either.
	const emailInputs = controls.filter((control) => control instanceof HTMLInputElement && control.type === 'email');
	if (emailInputs.length) return emailInputs.every((input) => EMAIL_PATTERN.test(input.value.trim()));

	// A phone field keeps its visible input prefilled with the dial code and writes the value
	// that actually gets submitted to a hidden input, so that hidden input is the one to read.
	if (controls.some((control) => control instanceof HTMLInputElement && control.type === 'tel')) {
		const hiddenValue = getTrimmedValue(field.querySelector('input[type="hidden"]'));
		if (hiddenValue !== null) return hiddenValue !== '';
	}

	// The label points at the field's own control, which keeps us from reading unrelated
	// inputs that a composite field may render alongside it.
	const labelFor = field.querySelector<HTMLLabelElement>('label[for]')?.htmlFor;
	const labelledValue = getTrimmedValue(labelFor ? field.ownerDocument.getElementById(labelFor) : null);
	if (labelledValue !== null) return labelledValue !== '';

	return controls.every((control) => control.value.trim() !== '');
}

function hasUnfilledRequiredFields(form: HTMLFormElement) {
	// isHidden is checked last so its computed style lookups only run for fields that would
	// otherwise block submission.
	return Array.from(form.querySelectorAll<HTMLElement>(FIELD_SELECTOR)).some((field) => isRequired(field) && !isFilled(field) && !isHidden(field));
}

export function useHubSpotSubmitGuard(containerRef: RefObject<HTMLDivElement>, formHtmlCode: string, isMounted: boolean) {
	useEffect(() => {
		if (!containerRef.current || !isMounted) return;

		const container = containerRef.current;
		let frame: number | null = null;

		function updateSubmitState() {
			frame = null;

			const form = container.querySelector('form');
			if (!form) return;

			const shouldDisable = hasUnfilledRequiredFields(form);
			form.querySelectorAll<HTMLButtonElement>('button[type="submit"]').forEach((button) => {
				// HubSpot disables the button itself while submitting - leave that alone so we
				// don't re-enable it mid-submit.
				if (button.getAttribute('aria-busy') === 'true' || button.classList.contains('hsfc-Button--loading')) return;
				if (button.disabled !== shouldDisable) {
					button.disabled = shouldDisable;
				}
			});
		}

		// HubSpot re-renders on every keystroke, so coalesce the checks into one per frame.
		function scheduleUpdate() {
			if (frame !== null) return;
			frame = requestAnimationFrame(updateSubmitState);
		}

		updateSubmitState();

		container.addEventListener('input', scheduleUpdate);
		container.addEventListener('change', scheduleUpdate);
		// The react-select replacement for checkbox groups toggles the native checkboxes with
		// a synthetic click, which neither input nor change events cover.
		container.addEventListener('click', scheduleUpdate);

		// Catches the form being injected, re-rendered, or having its disabled state reset.
		const observer = new MutationObserver(scheduleUpdate);
		observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });

		return () => {
			if (frame !== null) cancelAnimationFrame(frame);
			container.removeEventListener('input', scheduleUpdate);
			container.removeEventListener('change', scheduleUpdate);
			container.removeEventListener('click', scheduleUpdate);
			observer.disconnect();
		};
	}, [formHtmlCode, isMounted]);
}
